// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * A minimal first-party PDF writer — pure, unit-tested, zero dependencies.
 *
 * Tiltwell ships no runtime dependency, so there is no jsPDF here. It does not
 * need one: a PDF is a plain-text object container, a JPEG can be embedded
 * verbatim with /DCTDecode (no compression code), and the base-14 /Helvetica
 * needs no font embedding. That is enough for one page holding one annotated
 * photo and a few lines of text.
 */

/** Chars WinAnsiEncoding places outside Latin-1, mapped to their PDF codes. */
const WINANSI_EXTRA: Record<string, number> = {
  '…': 0x85, // …
  '‘': 0x91, // '
  '’': 0x92, // '
  '“': 0x93, // "
  '”': 0x94, // "
  '•': 0x95, // •
  '–': 0x96, // –
  '—': 0x97, // —
};

const A4_SHORT = 595;
const A4_LONG = 842;
const MARGIN = 40;

export interface PdfDoc {
  /** Baseline JPEG bytes, embedded verbatim as a /DCTDecode XObject. */
  jpeg: Uint8Array;
  imgW: number;
  imgH: number;
  title: string;
  lines: string[];
  footer: string;
}

export interface Size {
  width: number;
  height: number;
}

/**
 * Encode a string as a PDF literal string body: map to WinAnsi codes (anything
 * unrepresentable becomes "?") and backslash-escape the delimiters.
 */
export function pdfText(text: string): string {
  let out = '';
  for (const ch of text) {
    let code = WINANSI_EXTRA[ch];
    if (code === undefined) {
      const cp = ch.codePointAt(0)!;
      code = cp <= 0xff ? cp : 0x3f;
    }
    if (code < 0x20) code = 0x20;
    const c = String.fromCharCode(code);
    if (c === '(' || c === ')' || c === '\\') out += '\\';
    out += c;
  }
  return out;
}

/** A4, turned to landscape for a landscape photo so the image lands larger. */
export function pageSizeFor(imgW: number, imgH: number): Size {
  return imgW > imgH ? { width: A4_LONG, height: A4_SHORT } : { width: A4_SHORT, height: A4_LONG };
}

/** Contain-fit an image into a box, preserving aspect. Never enlarges past the box. */
export function fitBox(imgW: number, imgH: number, boxW: number, boxH: number): { w: number; h: number } {
  if (!(imgW > 0 && imgH > 0 && boxW > 0 && boxH > 0)) return { w: 0, h: 0 };
  const scale = Math.min(boxW / imgW, boxH / imgH);
  return { w: imgW * scale, h: imgH * scale };
}

function n(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** The page content stream: title, notes, the image, and a footer line. */
export function contentStream(doc: PdfDoc, page: Size): string {
  const ops: string[] = [];
  const titleBase = page.height - MARGIN - 14;
  ops.push('0.043 0.059 0.039 rg');
  ops.push(`BT /F2 18 Tf ${MARGIN} ${n(titleBase)} Td (${pdfText(doc.title)}) Tj ET`);

  let y = titleBase - 22;
  ops.push('0.29 0.38 0.29 rg');
  for (const line of doc.lines) {
    ops.push(`BT /F1 11 Tf ${MARGIN} ${n(y)} Td (${pdfText(line)}) Tj ET`);
    y -= 15;
  }

  const boxTop = y - 6;
  const boxBottom = MARGIN + 18;
  const { w, h } = fitBox(doc.imgW, doc.imgH, page.width - MARGIN * 2, boxTop - boxBottom);
  const x = (page.width - w) / 2;
  const imgY = boxTop - h;
  ops.push(`q ${n(w)} 0 0 ${n(h)} ${n(x)} ${n(imgY)} cm /Im0 Do Q`);

  ops.push('0.55 0.6 0.55 rg');
  ops.push(`BT /F1 9 Tf ${MARGIN} ${MARGIN} Td (${pdfText(doc.footer)}) Tj ET`);
  return ops.join('\n') + '\n';
}

function latin1(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

/**
 * Assemble the whole file: seven objects, a cross-reference table whose offsets
 * are counted in bytes (the JPEG is binary, so byte counting is not optional),
 * and the trailer.
 */
export function buildPdfBytes(doc: PdfDoc): Uint8Array<ArrayBuffer> {
  const page = pageSizeFor(doc.imgW, doc.imgH);
  const content = latin1(contentStream(doc, page));

  const objects: Array<Array<string | Uint8Array>> = [
    ['<< /Type /Catalog /Pages 2 0 R >>'],
    ['<< /Type /Pages /Kids [3 0 R] /Count 1 >>'],
    [
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${page.width} ${page.height}]` +
        ' /Resources << /XObject << /Im0 5 0 R >> /Font << /F1 6 0 R /F2 7 0 R >> >>' +
        ' /Contents 4 0 R >>',
    ],
    [`<< /Length ${content.length} >>\nstream\n`, content, '\nendstream'],
    [
      `<< /Type /XObject /Subtype /Image /Width ${doc.imgW} /Height ${doc.imgH}` +
        ` /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${doc.jpeg.length} >>\nstream\n`,
      doc.jpeg,
      '\nendstream',
    ],
    ['<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'],
    ['<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>'],
  ];

  const chunks: Uint8Array[] = [];
  let length = 0;
  const push = (chunk: string | Uint8Array): void => {
    const bytes = typeof chunk === 'string' ? latin1(chunk) : chunk;
    chunks.push(bytes);
    length += bytes.length;
  };

  // The binary comment tells readers the file is not plain 7-bit text.
  push('%PDF-1.4\n%âãÏÓ\n');
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(length);
    push(`${i + 1} 0 obj\n`);
    body.forEach(push);
    push('\nendobj\n');
  });

  const xrefAt = length;
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets) xref += `${String(offset).padStart(10, '0')} 00000 n \n`;
  push(xref);
  push(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}
